package pl.mothdigital.neuronowi.pages.therapies

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.selectors.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.resources.StringResources
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.navigation.*
import pl.mothdigital.neuronowi.theme.*

@Page
@Composable
fun TherapiesPage() {
    val context = rememberPageContext()
    var selectedCategory by remember { mutableStateOf<TherapyCategory?>(null) }

    PageLayout(
        title = StringResources.THERAPIES_PAGE_TITLE,
        contentAlignment = Alignment.TopStart,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.Top,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            VerticalSpacer(78.px)
            TherapiesHeaderSection()
            TherapiesCategoriesSection(selectedCategory) { selectedCategory = it }
            TherapiesSection(selectedCategory)
            CTASection(context)
        }
    }
}

@Composable
fun TherapiesHeaderSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 4.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH2 { Text("Nasze terapie".fixPolishOrphans()) }
            AppP(
                modifier = Modifier.maxWidth(700.px).textAlign(TextAlign.Center),
                color = SiteTheme.green._800
            ) {
                Text("W Centrum Terapii NeuroNowi oferujemy kompleksowe podejście – od terapii mowy po fizjoterapię i wsparcie psychologiczne. Poznaj nasze metody.".fixPolishOrphans())
            }
        }
    }
}

enum class TherapyCategory(val displayName: String) {
    LOGOPEDIC("Logopedyczne"),
    NEUROLOGIC("Neurologiczne"),
    PSYCHOLOGICAL("Psychologiczne"),
    MANUAL_SENSORY("Manualne i sensoryczne");
}

@Composable
fun TherapiesCategoriesSection(selectedCategory: TherapyCategory?, onCategorySelected: (TherapyCategory?) -> Unit) {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 1.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 1.cssRem)
                .flexWrap(FlexWrap.Wrap)
                .gap(1.cssRem),
            horizontalArrangement = Arrangement.Center,
        ) {
            CategoryButton(
                "Wszystkie",
                isSelected = selectedCategory == null,
                onClick = { onCategorySelected(null) }
            )
            
            TherapyCategory.entries.forEach { category ->
                CategoryButton(
                    category.displayName,
                    isSelected = selectedCategory == category,
                    onClick = { onCategorySelected(category) }
                )
            }
        }
    }
}

@Composable
fun CategoryButton(text: String, isSelected: Boolean, onClick: () -> Unit) {
    Button(
        attrs = Modifier
            .backgroundColor(if (isSelected) SiteTheme.green._700 else SiteTheme.green._100)
            .color(if (isSelected) SiteTheme.white._900 else SiteTheme.green._900)
            .borderRadius(50.px)
            .padding(leftRight = 1.cssRem, topBottom = 0.5.cssRem)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .toAttrs()
    ) {
        Text(text)
    }
}

val TherapyCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(SiteTheme.white._900)
            .boxShadow(blurRadius = 4.px, color = rgba(0, 0, 0, 0.1))
            .borderRadius(1.cssRem)
            .transition(Transition.of("all", 0.3.s))
    }
    hover {
        Modifier
            .backgroundColor(SiteTheme.white._800)
            .boxShadow(blurRadius = 16.px, color = rgba(0, 0, 0, 0.15))
            .cursor(Cursor.Pointer)
    }
}

@Composable
fun TherapiesSection(selectedCategory: TherapyCategory?) {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(2.cssRem)) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexWrap(FlexWrap.Wrap)
                    .gap(2.cssRem),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.Top
            ) {
                if (selectedCategory == null || selectedCategory == TherapyCategory.LOGOPEDIC) {
                    TherapyCard(
                        icon = { FaBook() },
                        name = "Terapia logopedyczna",
                        description = "Kompleksowa diagnoza i terapia zaburzeń mowy, komunikacji oraz funkcji prymarnych u dzieci i dorosłych.",
                        screen = Screen.TherapyLogopedic,
                        category = TherapyCategory.LOGOPEDIC
                    )
                    
                    TherapyCard(
                        icon = { FaFaceSmile() },
                        name = "Terapia miofunkcjonalna",
                        description = "Przywracanie równowagi mięśniowej w obrębie jamy ustnej oraz prawidłowych wzorców oddychania i połykania.",
                        screen = Screen.TherapyMiofunctional,
                        category = TherapyCategory.LOGOPEDIC
                    )
                    
                    TherapyCard(
                        icon = { FaEarDeaf() },
                        name = "Terapia słuchowa",
                        description = "Diagnoza i terapia centralnych zaburzeń przetwarzania słuchowego, które wpływają na komunikację i uczenie się.",
                        screen = Screen.TherapyAuditory,
                        category = TherapyCategory.LOGOPEDIC
                    )
                }
                
                if (selectedCategory == null || selectedCategory == TherapyCategory.NEUROLOGIC) {
                    TherapyCard(
                        icon = { FaBrain() },
                        name = "Terapia neurotaktylna",
                        description = "Stymulacja układu nerwowego poprzez receptory dotykowe, wspierająca integrację odruchów pierwotnych.",
                        screen = Screen.TherapyNeurotactile,
                        category = TherapyCategory.NEUROLOGIC
                    )
                }
                
                if (selectedCategory == null || selectedCategory == TherapyCategory.PSYCHOLOGICAL) {
                    TherapyCard(
                        icon = { FaHeart() },
                        name = "Wsparcie psychologiczne",
                        description = "Pomoc w rozwijaniu umiejętności społecznych, radzeniu sobie z emocjami oraz budowaniu poczucia własnej wartości.",
                        screen = Screen.TherapyPsychological,
                        category = TherapyCategory.PSYCHOLOGICAL
                    )
                }
                
                if (selectedCategory == null || selectedCategory == TherapyCategory.MANUAL_SENSORY) {
                    TherapyCard(
                        icon = { FaHandsHolding() },
                        name = "Terapia ręki",
                        description = "Rozwijanie sprawności manualnej, koordynacji wzrokowo-ruchowej oraz precyzji ruchów dłoni i palców.",
                        screen = Screen.TherapyHand,
                        category = TherapyCategory.MANUAL_SENSORY
                    )
                    
                    TherapyCard(
                        icon = { FaPuzzlePiece() },
                        name = "Integracja sensoryczna",
                        description = "Diagnoza i terapia zaburzeń przetwarzania sensorycznego, wspierająca prawidłowy rozwój dziecka.",
                        screen = Screen.TherapySensoryIntegration,
                        category = TherapyCategory.MANUAL_SENSORY
                    )
                    
                    TherapyCard(
                        icon = { FaPersonRunning() },
                        name = "Fizjoterapia",
                        description = "Diagnoza i rehabilitacja układu ruchu, terapia bólu oraz usprawnianie po urazach i zabiegach operacyjnych.",
                        screen = Screen.TherapyPhysio,
                        category = TherapyCategory.MANUAL_SENSORY
                    )
                }
            }
        }
    }
}

@Composable
fun TherapyCard(icon: @Composable () -> Unit, name: String, description: String, screen: Screen, category: TherapyCategory) {
    Link(
        path = screen.route,
        modifier = TherapyCardStyle.toModifier()
            .width(350.px)
            .padding(1.5.cssRem)
            .tabIndex(0),
        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Top,
        ) {
            Div(
                attrs = Modifier
                    .backgroundColor(SiteTheme.green._50)
                    .color(SiteTheme.green._800)
                    .fontSize(2.cssRem)
                    .padding(1.cssRem)
                    .borderRadius(50.percent)
                    .margin(bottom = 1.cssRem)
                    .toAttrs()
            ) {
                icon()
            }
            
            AppH3(modifier = Modifier.textAlign(TextAlign.Center)) { Text(name.fixPolishOrphans()) }
            VerticalSpacer(10.px)
            AppP(modifier = Modifier.textAlign(TextAlign.Center)) { Text(description.fixPolishOrphans()) }
        }
    }
}
